'use client';

import type { ReactNode } from 'react';
import { Button } from '@/components/ui/forms/button';
import { PanelDialog } from '@/components/ui/layout/PanelDialog';
import { ResumeIntakePanel, type ResumeIntakePanelProps } from './ResumeIntakePanel';

export interface ResumeIntakeDialogProps extends ResumeIntakePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Eyebrow uppercase label above the title. Default: "Resume Library". */
  eyebrow?: string;
  /** Big title in the dialog header. Default: "My Resumes". */
  title?: string;
  /** Optional highlighted accent rendered after the title (e.g. featured role). */
  accent?: ReactNode;
  /** Muted description under the title. Default: library copy. */
  description?: string;
  /** Footer cancel-button label. Default: "Cancel". */
  cancelLabel?: string;
  /** Desktop header chrome. Defaults to "compact" for a minimal title row. Pass "rich" to show eyebrow + large title + accent. */
  desktopHeaderVariant?: 'rich' | 'compact';
}

export function ResumeIntakeDialog({
  open,
  onOpenChange,
  eyebrow = 'Resume Library',
  title = 'My Resumes',
  accent,
  description = 'Create, upload, or open one of your saved resumes.',
  cancelLabel = 'Cancel',
  desktopHeaderVariant = 'compact',
  // Panel props pass through
  ...panelProps
}: ResumeIntakeDialogProps) {
  const footer = (
    <Button
      variant="outline"
      onClick={() => onOpenChange(false)}
      className="w-full border"
    >
      {cancelLabel}
    </Button>
  );

  return (
    <PanelDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      eyebrow={eyebrow}
      accent={accent}
      description={description}
      desktopHeaderVariant={desktopHeaderVariant}
      footer={footer}
    >
      <ResumeIntakePanel {...panelProps} onClose={() => onOpenChange(false)} />
    </PanelDialog>
  );
}
